/**
 * AddChildDialog.jsx - "Add PO line" / "Add sub-project" (10/14/26). A FANUC
 * PO with several lines meant creating a near-duplicate record by hand for
 * every line; now the new record is made from the one it belongs to, put
 * under it (Main project = that record), and only the new PO is asked for.
 *
 *   line  another PO line: same Global Project Number, same quote # - PO #,
 *         PO value, PO date, description asked
 *   sub   a new sub-project: a suggested next number (P26006-040, editable),
 *         its name, quote #, PO and description
 *
 * What is copied comes from the server (GET /api/projects/:id/child-seed -
 * the assistant's duplicate list less the money, the PO and the invoicing);
 * the record is then created with the ordinary POST (AppContext.createProject),
 * so the quote guard, the Activity log and its Revert, and live updates all
 * work as for any new record. Main tab only.
 */
import { useEffect, useState } from 'react';
import { Modal } from '../../ui/Modal';
import { useApp } from '../../../context/AppContext';
import { BTN_CANCEL, BTN_PRIMARY, inputSt, labelSt, rowSt } from '../../opportunities/editOpportunityConstants';
import { OPP_FIELD_LABELS, fieldLabel } from '../../opportunities/editOpportunityForm';

const TITLES = { line: 'Add PO line', sub: 'Add sub-project' };
const camel = (k) => String(k).replace(/_([a-z])/g, (_, c) => c.toUpperCase());

export async function fetchChildSeed(id, kind) {
  try {
    const q = new URLSearchParams({ kind });
    const r = await fetch(`/api/projects/${encodeURIComponent(id)}/child-seed?${q}`);
    const body = await r.json().catch(() => null);
    return r.ok && body ? { ok: true, seed: body } : { ok: false, error: (body && body.error) || 'Could not read the record.' };
  } catch {
    return { ok: false, error: 'Could not reach the server.' };
  }
}

/**
 * Props: source (the record it is made from), kind, onClose, onCreated(row).
 */
export default function AddChildDialog({ source, kind = 'line', onClose, onCreated }) {
  const { createProject } = useApp();
  const [seed, setSeed] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ poNumber: '', poValue: '', poDate: '', description: '', name: '', gpn: '', quote: '' });
  const set = (k) => (e) => {
    const v = e.target.value;
    setF((p) => ({ ...p, [k]: v }));
    setError('');
  };

  useEffect(() => {
    let live = true;
    fetchChildSeed(source.id, kind).then((r) => {
      if (!live) return;
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setSeed(r.seed);
      setF((p) => ({
        ...p,
        gpn: r.seed.body.global_project_number || '',
        name: p.name || r.seed.body.name || '',
      }));
    });
    return () => {
      live = false;
    };
  }, [source.id, kind]);

  const save = async () => {
    if (!seed || busy) return;
    if (!f.poNumber.trim() && !f.description.trim()) {
      setError('Enter the new PO # (or at least a description).');
      return;
    }
    if (f.poValue.trim() && !Number.isFinite(Number(f.poValue.replace(/[$,\s]/g, '')))) {
      setError('The PO value must be a number.');
      return;
    }
    if (kind === 'sub' && !f.name.trim()) {
      setError('Give it a name.');
      return;
    }
    const body = { ...seed.body };
    if (f.poNumber.trim()) body.po_number = f.poNumber.trim();
    if (f.poValue.trim()) body.po_value = Number(f.poValue.replace(/[$,\s]/g, ''));
    if (f.poDate) body.po_date_issued = f.poDate;
    if (f.description.trim()) body.description = f.description.trim();
    if (kind === 'sub') {
      body.name = f.name.trim();
      if (f.gpn.trim()) body.global_project_number = f.gpn.trim().toUpperCase();
      if (f.quote.trim()) body.quote_letter = f.quote.trim();
    }
    // a PO line is another line of the same quote; a sub-project's own quote is checked as usual
    const sameQuote =
      kind === 'line' ||
      (body.quote_letter && String(body.quote_letter).trim() === String(source.quoteLetter || source.quote_letter || '').trim());
    setBusy(true);
    try {
      const r = await createProject(body, { allowDuplicateQuote: !!sameQuote });
      if (!r.ok || !r.body) {
        setError((r.body && r.body.error) || 'Could not save - try again.');
        return;
      }
      if (onCreated) onCreated(r.body);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, extra = {}) => (
    <div style={rowSt}>
      <div style={labelSt}>{label}</div>
      <input aria-label={label} style={{ ...inputSt, flex: 1, minWidth: 0 }} value={f[key]} onChange={set(key)} {...extra} />
    </div>
  );
  const from = [
    source.globalProjectNumber || source.global_project_number,
    source.name || source.description,
    source.poNumber || source.po_number,
  ]
    .filter(Boolean)
    .join(' · ');
  const copied = seed ? seed.copied.map((k) => fieldLabel(camel(k), OPP_FIELD_LABELS)) : [];

  return (
    <Modal
      title={TITLES[kind]}
      onClose={busy ? undefined : onClose}
      width={560}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={BTN_CANCEL} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" style={{ ...BTN_PRIMARY, opacity: !seed || busy ? 0.6 : 1 }} onClick={save} disabled={!seed || busy}>
            {busy ? 'Saving...' : TITLES[kind]}
          </button>
        </div>
      }
    >
      <div style={{ fontSize: 12, color: '#444', lineHeight: 1.5, marginBottom: 10 }}>
        {kind === 'line' && (
          <>
            A new record for another line of this PO, put under <b>{from}</b> (its Main project).
          </>
        )}
        {kind === 'sub' && (
          <>
            A new sub-project, put under <b>{from}</b> (its Main project). The number is only a suggestion.
          </>
        )}
      </div>
      {kind === 'sub' && field('Global Project #', 'gpn', { placeholder: 'e.g. P26006-040' })}
      {kind === 'sub' && field('Name', 'name', { placeholder: 'e.g. Oshkosh 3003313 Additional Paint Support' })}
      {kind === 'sub' && field('Quote # (optional)', 'quote', { placeholder: 'its own quote' })}
      {field('PO #', 'poNumber', { placeholder: 'e.g. 100200836Ln2', autoFocus: true })}
      {field('PO value', 'poValue', { placeholder: '0', inputMode: 'decimal' })}
      {field('PO date', 'poDate', { type: 'date' })}
      {field('Description', 'description', { placeholder: 'e.g. expenses, labor, Paint support extension' })}
      {seed && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 10px',
            background: '#F5F8F1',
            border: '1px solid #E3EEDA',
            borderRadius: 6,
            fontSize: 11,
            color: '#555',
            lineHeight: 1.45,
          }}
        >
          <b>Copied:</b> {copied.join(', ') || 'nothing'}. <b>Not copied:</b> the money and PO (asked above), invoicing, tracking, comments,
          tasks and files.
          {seed.body.global_project_number && kind === 'line' && (
            <>
              {' '}
              Project # <b>{seed.body.global_project_number}</b>.
            </>
          )}
        </div>
      )}
      {!seed && !error && <div style={{ fontSize: 11.5, color: '#888' }}>Reading the record...</div>}
      {error && (
        <div role="alert" style={{ marginTop: 10, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
